"use client";

import { useEffect, useRef, useState } from "react";
import { useStation } from "@/lib/station-context";

// Which station the dashboard is working on. With one station it's just a
// label; with several it opens a list (only the ones this account may use).
export function StationSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { stations, station, select } = useStation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!station) return null;
  const several = stations.length > 1;

  return (
    <div ref={box} className={`relative border-b border-sidebar-border py-3 ${collapsed ? "px-2" : "px-3"}`}>
      {!collapsed && (
        <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-text">Station</div>
      )}
      <button
        type="button"
        disabled={!several}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={collapsed ? station.name : undefined}
        aria-label={collapsed ? `Station: ${station.name}` : undefined}
        className={`flex w-full items-center rounded-[10px] bg-white/[0.06] py-2 text-left text-sm font-semibold text-white enabled:hover:bg-white/10 disabled:cursor-default ${
          collapsed ? "justify-center px-0" : "gap-2.5 px-3"
        }`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-blue text-[11px] font-bold">
          {station.name
            .split(/\s+/)
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
        </span>
        <span className={`min-w-0 flex-1 ${collapsed ? "hidden" : ""}`}>
          <span className="block truncate">{station.name}</span>
          {station.genre && <span className="block truncate text-[11px] font-normal text-sidebar-text">{station.genre}</span>}
        </span>
        {several && !collapsed && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 text-sidebar-text">
            <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
          </svg>
        )}
      </button>
      {open && several && (
        <ul
          role="listbox"
          aria-label="Choose a station"
          className={`absolute z-50 max-h-72 overflow-y-auto rounded-xl border border-sidebar-border bg-sidebar-bg p-1 shadow-xl ${
            collapsed ? "left-full top-2 ml-2 w-56" : "left-3 right-3 top-full mt-1"
          }`}
        >
          {stations.map((s) => (
            <li key={s.id} role="option" aria-selected={s.id === station.id}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  select(s.id);
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10 ${
                  s.id === station.id ? "bg-white/10 font-semibold" : ""
                }`}
              >
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {s.id === station.id && <span className="text-accent-blue">●</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
